import type { CSSProperties } from 'react';
import { isValidCardCode, parseCardCode } from 'lib/card-utils';

function SuitMark({ suit, red }: { suit: string; red: boolean }) {
  return (
    <span className={`tp-card-suit ${red ? 'tp-card-suit-red' : 'tp-card-suit-black'}`} aria-hidden>
      {suit}
    </span>
  );
}

interface PlayingCardProps {
  code: string;
  hidden?: boolean;
  small?: boolean;
  medium?: boolean;
  large?: boolean;
  compact?: boolean;
  fanIndex?: number;
  flatLayout?: boolean;
}

/** Open cards — slight fan with overlap (matches game table) */
const FAN_OPEN = [
  { rotate: -15, zIndex: 1 },
  { rotate: 0, zIndex: 2 },
  { rotate: 15, zIndex: 3 },
] as const;

function fanSlotStyle(index: number): CSSProperties {
  const slot = FAN_OPEN[Math.min(2, Math.max(0, index))];
  return {
    position: 'relative',
    display: 'inline-block',
    flexShrink: 0,
    marginLeft: index === 0 ? 0 : 'calc(-1 * var(--tp-fan-overlap, 1.05rem))',
    transform: `rotate(${slot.rotate}deg)`,
    transformOrigin: '50% 100%',
    zIndex: slot.zIndex,
  };
}

function CardCorner({ rank, suit, red }: { rank: string; suit: string; red: boolean }) {
  return (
    <div className="tp-card-corner tp-card-corner-tl" aria-hidden>
      <span className={`tp-card-rank ${red ? 'tp-card-rank-red' : 'tp-card-rank-black'}`}>{rank}</span>
      <SuitMark suit={suit} red={red} />
    </div>
  );
}

function CardFace({ rank, suit, red }: { rank: string; suit: string; red: boolean }) {
  const suitClass = red ? 'tp-card-suit-red' : 'tp-card-suit-black';
  return (
    <div className="tp-card-face absolute inset-0 rounded-[inherit]">
      <div className="tp-card-face-paper" aria-hidden />
      <div className="tp-card-face-shine" aria-hidden />
      <div className="tp-card-face-frame" aria-hidden />
      <CardCorner rank={rank} suit={suit} red={red} />
      <div className="tp-card-face-center" aria-hidden>
        <span className={`tp-card-face-watermark ${suitClass}`}>{suit}</span>
      </div>
    </div>
  );
}

export default function PlayingCard({
  code,
  hidden,
  small,
  medium,
  large,
  compact,
  fanIndex = 1,
  flatLayout = false,
}: PlayingCardProps) {
  const faceUp = !hidden && code !== 'back' && isValidCardCode(code);
  const parsed = faceUp ? parseCardCode(code) : null;
  const { rank = '', suit = '', red = false } = parsed ?? {};
  const size = compact
    ? large
      ? 'tp-card-size-hero'
      : medium || small
        ? 'tp-card-size-opp'
        : 'tp-card-size-opp'
    : large
      ? 'h-[88px] w-[62px] sm:h-[96px] sm:w-[68px]'
      : small
        ? 'h-[58px] w-[40px] sm:h-[64px] sm:w-[44px]'
        : medium
          ? 'h-[72px] w-[50px] sm:h-[78px] sm:w-[54px]'
          : 'h-[79px] w-[56px] lg:h-[86px] lg:w-[61px]';

  const card = (
    <div
      className={`${size} tp-playing-card tp-card-premium relative shrink-0 overflow-hidden tp-card-face-up`}
    >
      {faceUp && <CardFace rank={rank} suit={suit} red={red} />}
    </div>
  );

  if (flatLayout) {
    return card;
  }

  return (
    <div className="tp-card-fan-slot tp-card-fan-slot-open" style={fanSlotStyle(fanIndex)}>
      {card}
    </div>
  );
}
